import { App as CapApp } from "@capacitor/app";
import { useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { storageKey } from "./configs/config.ts";
import { theme } from "./configs/theme.ts";
import { usePwaViewportWidth } from "./hooks/useScreenWidth.tsx";
import { PushNotificationHandler } from "./components/PushNotificationHandler.tsx";
import { NavLayout } from "./layouts/Nav.layout.tsx";
import { GlobalSocketHandler } from "./components/GlobalSocketHandler.tsx";

import LandingPage from "./pages/auth/Landing.page.tsx";
import LogInPage from "./pages/auth/LogIn.page.tsx";
import ResetPasswordPage from "./pages/auth/ResetPassword.page.tsx";
import SignUpPage from "./pages/auth/SignUp.page.tsx";
import ChatPage from "./pages/Chat.page.tsx";
import ChatWithPage from "./pages/ChatWith.page.tsx";
import FirstVisitPage from "./pages/FirstVisit.page";
import InformationsPage from "./pages/Informations.page.tsx";
import CynoMethodPage from "./pages/informations/CynoMethod.page.tsx";
import DogBodyLanguagePage from "./pages/informations/DogBodyLanguage.page.tsx";
import FaqPage from "./pages/informations/Faq.page.tsx";
import EnrichmentExercisesPage from "./pages/informations/TipsAndAdvice.page.tsx";
import MatchHappeningPage from "./pages/MatchHappening.page.tsx";
import AboutThemPage from "./pages/onboarding/AboutThem.page.tsx";
import AboutYouConnectWithPage from "./pages/onboarding/AboutYouConnectWith.page.tsx";
import AddPicturePage from "./pages/onboarding/AddPictures.page.tsx";
import ProfilePage from "./pages/Profile.page.tsx";
import HighlightsPage from "./pages/profile/Highlight.page.tsx";
import PicturePage from "./pages/profile/Pictures.page.tsx";
import UpdateAboutYouConnectWithPage from "./pages/profile/UpdateAboutYouConnectWith.page.tsx";
import UpdateQuestionsPage from "./pages/profile/UpdateQuestions.page.tsx";
import ProfileViewPage from "./pages/ProfileView.page.tsx";
import SettingsPage from "./pages/Settings.page.tsx";
import DistancePage from "./pages/settings/Distance.page.tsx";
import NotificationsPage from "./pages/settings/Notifications.page.tsx";
import TermOfUsePage from "./pages/settings/TermOfUse.page copy.tsx";
import StaffMatchHappeningPage from "./pages/StaffMatchHappening.page.tsx";
import SwiperPage from "./pages/Swiper.page.tsx";
import VerifyAccountPage from "./pages/VerifyAccount.page.tsx";
import WharningMatchPage from "./pages/WarningMatchPage.page.tsx";
import { PrivateRoute, ProfileRoute, ProtectedRoute } from "./ProtectedRoute.tsx";
import api from "./services/api.ts";
import "./services/mixpanel.ts";
import { initUTMTracking } from "./services/mixpanel.ts";
import "./styles/App.style.tsx";
import { RootContainer } from "./styles/App.style.tsx";
import { GlobalStyle } from "./styles/Global.style.tsx";

const DebugNavigation = () => {
  const location = useLocation();

  useEffect(() => {
    console.log("Navigated to:", location.pathname);
  }, [location]);

  return null; // This component just logs navigation
};

const TriggerEmailAnalytics = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token"); // ✅ Get token from URL

  // Trigger analytics: new_message, new_match
  useEffect(() => {
    (async function trackClickEmail() {
      if (token && !sessionStorage.getItem(`clicked-email-${token}`)) {
        await api.trackClickEmail(token);
        sessionStorage.setItem(`clicked-email-${token}`, "true");
      }
    })();
  }, []);

  return null; // This component just trigger analytics
};

const TriggerAppOpenURL = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Handle the cold start link
    (async () => {
      const launch = await CapApp.getLaunchUrl();
      if (launch?.url) {
        handleUrl(launch.url);
      }
    })();

    // Handle when app already open
    const handler = (e: CustomEvent<string>) => {
      handleUrl(e.detail);
    };

    window.addEventListener("app-url-open", handler as EventListener);
    return () => window.removeEventListener("app-url-open", handler as EventListener);
  }, [navigate]);

  const handleUrl = (url: string) => {
    console.log("Handling URL:", url);

    if (url.includes(routeList.resetPassword)) {
      const parsed = new URL(url);
      const token = parsed.searchParams.get("token");

      navigate(`/reset-password?token=${token}`);
    }
  };

  return null;
};

// ########## APP ###################

function App() {
  const viewportWidth = usePwaViewportWidth();
  useEffect(() => {
    initUTMTracking();
    // ### SENTRY TEST SNIPPETS ###
    // (window as any).forceSentryError(); // Trigger a JS error
    // SentryCapacitor.nativeCrash();    // Trigger a native crash (Mobile only) -> Re-open the app after the crash to send the cached report to Sentry
  }, []);

  // Important here to allow the user location to update on page refresh
  useEffect(() => {
    console.log(`DELETE ${storageKey.userLocation} ON PAGE RELOAD`);
    sessionStorage.removeItem(storageKey.userLocation);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <RootContainer $width={viewportWidth}>
        <Router>
          <PushNotificationHandler />
          <GlobalSocketHandler />
          <DebugNavigation />

          <TriggerAppOpenURL />
          <TriggerEmailAnalytics />
          <Routes>
            {/* First visit page */}
            <Route path="/" element={<FirstVisitPage />} />

            {/* Auth pages */}
            <Route element={<ProtectedRoute />}>
              <Route path={routeList.landing} element={<LandingPage />} />
              <Route path={routeList.signup} element={<SignUpPage />} />
              <Route path={routeList.login} element={<LogInPage />} />
              <Route path={routeList.resetPassword} element={<ResetPasswordPage />} />
            </Route>

            <Route element={<PrivateRoute />}>
              <Route element={<ProfileRoute />}>
                <Route path={routeList.addPictures} element={<AddPicturePage />} />
                <Route path={routeList.aboutThem} element={<AboutThemPage />} />
                <Route path={routeList.aboutYouConnectWith} element={<AboutYouConnectWithPage />} />
                <Route path={routeList.verifyAccount} element={<VerifyAccountPage />} />
                <Route path={routeList.staffMatchHappening} element={<StaffMatchHappeningPage />} />

                <Route element={<NavLayout />}>
                  <Route path={routeList.swiper} element={<SwiperPage />} />
                  <Route path={`${routeList.profileView}/:profileId`} element={<ProfileViewPage />} />

                  {/* Information pages */}
                  <Route path={routeList.informations} element={<InformationsPage />} />
                  <Route path={informationsRouteList.theCynoMethod} element={<CynoMethodPage />} />
                  <Route path={informationsRouteList.dogBodyLanguage} element={<DogBodyLanguagePage />} />
                  <Route path={informationsRouteList.tipsAndAdvice} element={<EnrichmentExercisesPage />} />
                  <Route path={informationsRouteList.faq} element={<FaqPage />} />

                  <Route path={routeList.chat} element={<ChatPage />} />
                  <Route path={`${routeList.chatWith}/:chatId`} element={<ChatWithPage />} />
                  <Route path={routeList.warningMatch} element={<WharningMatchPage />} />

                  {/* Profile pages */}
                  <Route path={routeList.profile} element={<ProfilePage />} />
                  <Route path={profileRouteList.highlight} element={<HighlightsPage />} />
                  <Route path={profileRouteList.picture} element={<PicturePage />} />
                  <Route path={profileRouteList.aboutThem} element={<UpdateQuestionsPage />} />
                  <Route path={profileRouteList.aboutYouConnectWith} element={<UpdateAboutYouConnectWithPage />} />

                  {/* Settings pages */}
                  <Route path={routeList.settings} element={<SettingsPage />} />
                  <Route path={settingsRouteList.distance} element={<DistancePage />} />
                  <Route path={settingsRouteList.notifications} element={<NotificationsPage />} />
                  <Route path={settingsRouteList.termOfUse} element={<TermOfUsePage />} />

                  <Route path={routeList.settings} element={<SettingsPage />} />
                  <Route path={routeList.matchHappening} element={<MatchHappeningPage />} />
                </Route>
              </Route>
            </Route>

            {/* Catch-all for unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </RootContainer>
    </ThemeProvider>
  );
}

export const routeList = {
  signup: "/sign-up",
  login: "/log-in",
  landing: "/landing",
  addPictures: "/add-pictures",
  aboutThem: "/about-them",
  aboutYouConnectWith: "/about-you-connect-with",
  resetPassword: "/reset-password",
  verifyAccount: "/verify-account",
  swiper: "/swiper",
  informations: "/informations",
  chat: "/chat",
  chatWith: "/chat/chat-with",
  profile: "/profile",
  settings: "/settings",
  matchHappening: "/match-happening",
  staffMatchHappening: "/staff-match-happening",
  warningMatch: "/warning-match",
  profileView: "/profile-view",
};

export const informationsRouteList = {
  theCynoMethod: "/informations/the-cyno-method",
  dogBodyLanguage: "/informations/dog-body-language",
  tipsAndAdvice: "/informations/tips-and-advice",
  faq: "/informations/faq",
};

export const profileRouteList = {
  highlight: "/profile/highlight",
  picture: "/profile/picture",
  aboutThem: "/profile/aboutThem",
  aboutYouConnectWith: "/profile/about-you-connect-with",
};

export const settingsRouteList = {
  distance: "/settings/distance",
  notifications: "/settings/notifications",
  termOfUse: "/settings/term-of-use",
};

export default App;
